import React from 'react'

import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import {
  WizardStep as DeprecatedWizardStep,
  WizardStepRenderParams as DeprecatedWizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { NetsuiteWizardData } from 'modules/integrations_netsuite/lib/types'

type NetSuiteWizardStep = DeprecatedWizardStep<NetsuiteWizardData>
type NetSuiteWizardStepRenderParams = DeprecatedWizardStepRenderParams<NetsuiteWizardData>

const ACCOUNT_ID_EXAMPLE = '1234567'

const AccountIdUrlIllustration = () => (
  <F0Box
    width="full"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    padding="xl"
    background="secondary"
    borderRadius="md"
    role="img"
    aria-label={i18n.t(
      'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.image_alt'
    )}
  >
    <F0Box
      width="full"
      display="flex"
      flexDirection="column"
      padding="lg"
      border="default"
      borderColor="secondary"
      borderRadius="md"
      background="primary"
    >
      <F0Box display="flex" gap="xs" paddingBottom="md" aria-hidden="true">
        {[0, 1, 2].map((dot) => (
          <F0Box key={dot} width="2" height="2" borderRadius="xl" background="tertiary" />
        ))}
      </F0Box>

      <F0Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="default"
        borderColor="secondary"
        borderRadius="xl"
        padding="sm"
        background="secondary"
      >
        <F0Text as="span" variant="body" color="secondary" content="https://" />
        <F0Box
          display="flex"
          alignItems="center"
          paddingX="xs"
          border="default"
          borderColor="info"
          borderRadius="sm"
          background="info"
        >
          <F0Text as="span" variant="label" content={ACCOUNT_ID_EXAMPLE} />
        </F0Box>
        <F0Text as="span" variant="body" color="secondary" content=".app.netsuite.com/..." />
      </F0Box>
    </F0Box>

    <F0Box width="0" height="6" borderLeft="default" borderColor="info" aria-hidden="true" />

    <F0Box
      width="full"
      display="flex"
      flexDirection="column"
      gap="xs"
      padding="lg"
      border="default"
      borderColor="info"
      borderRadius="md"
      background="primary"
    >
      <F0Text
        variant="label"
        content={i18n.t(
          'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.callout_title'
        )}
      />
      <F0Text
        variant="body"
        content={i18n.t(
          'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.callout_description'
        )}
      />
    </F0Box>
  </F0Box>
)

const AccountIdStep = ({ data, onDataChanged }: NetSuiteWizardStepRenderParams) => {
  const session = useCurrentSession()

  useOnNext(() => {
    track(trackingEvents.integrationsNetsuite.netsuiteSettingsAccountIdEntered, {
      company_id: session.company.id,
      access_id: session.access.id,
    })
  })

  const handleChange = (value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        account_id: value,
      },
    })
  }

  return (
    <F0Box display="flex" flexDirection="row" gap="2xl">
      <F0Box display="flex" flexDirection="column" gap="lg" maxWidth="screen-sm">
        <F0Box display="flex" flexDirection="column" gap="xs">
          <F0Heading
            as="h2"
            variant="heading"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.title'
            )}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t(
              'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.description'
            )}
          />
        </F0Box>
        <TextField
          label={i18n.t(
            'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.label'
          )}
          value={data.answers.account_id || ''}
          onChange={handleChange}
        />
      </F0Box>
      <F0Box width="96" display="flex" flexDirection="column" gap="md">
        <AccountIdUrlIllustration />
        <F0Text
          variant="body"
          color="secondary"
          content={i18n.t(
            'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.hint'
          )}
        />
      </F0Box>
    </F0Box>
  )
}

export const accountIdStep = (): NetSuiteWizardStep => ({
  title: i18n.t(
    'app_store_v2.app_integrations.integrations_netsuite.settings.steps.account_id.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <AccountIdStep {...props} />,
  isCompleted: (data) => !!data.answers.account_id,
})
